import { lazy } from "react";

/**
 * React.lazy that survives a deploy.
 *
 * Every route in this app is code-split, so the browser fetches a chunk when
 * you navigate. The chunk filenames carry a content hash, and a deploy
 * replaces them. Anyone with the app already open is holding an index.html
 * that names chunks the server no longer has — so their next navigation throws
 * ChunkLoadError and, with no error boundary, takes the whole app to a blank
 * page. Not an edge case: it happens to every open tab on every deploy.
 *
 * The recovery is a reload, because the only fix is to fetch the new
 * index.html. It is guarded so a chunk that is genuinely missing (a bad build,
 * a CDN 404) cannot put the page in a reload loop: one attempt per module per
 * tab, remembered in sessionStorage. A second failure falls through to the
 * error boundary, which can then say something true instead of retrying
 * forever.
 */
const RELOAD_KEY = "nextopp:chunk-reload";

// webpack rejects with a ChunkLoadError; some browsers surface it only as a
// message about a failed dynamic import. Matching on both, because getting
// this wrong in the permissive direction means reloading on ordinary
// application errors — which would hide real bugs behind a refresh.
const isChunkError = (err) => {
  const name = err?.name || "";
  const message = err?.message || "";
  return name === "ChunkLoadError"
    || /Loading chunk .* failed/i.test(message)
    || /Loading CSS chunk .* failed/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
};

const attempted = (key) => {
  try {
    return sessionStorage.getItem(`${RELOAD_KEY}:${key}`) === "1";
  } catch {
    // Private mode, or storage disabled. Treat as "already tried" rather than
    // risking a loop we have no way to remember breaking out of.
    return true;
  }
};

const markAttempted = (key) => {
  try {
    sessionStorage.setItem(`${RELOAD_KEY}:${key}`, "1");
  } catch { /* nothing to do; the branch above handles it */ }
};

const clearAttempt = (key) => {
  try {
    sessionStorage.removeItem(`${RELOAD_KEY}:${key}`);
  } catch { /* ignore */ }
};

export function lazyWithRetry(importer, key) {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Loaded fine, so forget any earlier failure: the next deploy has to be
      // able to recover too, and a stale flag would deny it the one reload.
      clearAttempt(key);
      return mod;
    } catch (err) {
      if (!isChunkError(err) || attempted(key)) throw err;
      markAttempted(key);
      window.location.reload();
      // Never resolves. The reload replaces this document, and returning
      // anything here would briefly render a route we know is broken.
      return new Promise(() => {});
    }
  });
}

export const __testing = { isChunkError, RELOAD_KEY };
